/**
 * API Client for VORTEX_CA
 * 
 * Type-safe API client using openapi-fetch with generated types from OpenAPI spec.
 * All API calls go through this client to ensure consistent error handling,
 * authentication headers, and correlation ID tracking.
 *
 * Sprint 31: Added retry/backoff middleware for resilience.
 */
import createClient from 'openapi-fetch';
import type { paths } from './schema';

// Get base URL from environment or use relative path (proxied in dev)
const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/**
 * Retry configuration for failed requests
 * Uses exponential backoff with jitter
 */
export interface RetryConfig {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries: number;
  /** Base delay in milliseconds (default: 1000) */
  baseDelay: number;
  /** Maximum delay in milliseconds (default: 10000) */
  maxDelay: number;
  /** HTTP status codes that should trigger a retry (default: [408, 429, 500, 502, 503, 504]) */
  retryableStatusCodes: number[];
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateBackoff(attempt: number, config: RetryConfig): number {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = config.baseDelay * Math.pow(2, attempt);
  // Add jitter (±25%)
  const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
  // Clamp to maxDelay
  return Math.min(exponentialDelay + jitter, config.maxDelay);
}

/**
 * Create type-safe API client instance
 * 
 * The client automatically handles:
 * - Type-safe request/response bodies
 * - Path parameters
 * - Query parameters
 * - Headers
 */
export const apiClient = createClient<paths>({
  baseUrl,
});

/**
 * Fetch with retry logic for resilience
 * Sprint 31: Use this for critical operations that should retry on failure
 *
 * @param url - URL to fetch
 * @param options - Fetch options
 * @param config - Retry configuration (uses defaults if not provided)
 * @returns Response promise
 */
export async function fetchWithRetry(
  url: string,
  options?: RequestInit,
  config: Partial<RetryConfig> = {}
): Promise<Response> {
  const fullConfig = { ...defaultRetryConfig, ...config };
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= fullConfig.maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);

      // Check if we should retry based on status code
      if (
        fullConfig.retryableStatusCodes.includes(response.status) &&
        attempt < fullConfig.maxRetries
      ) {
        // Retry after backoff delay
        const delay = calculateBackoff(attempt, fullConfig);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Network errors should retry (e.g., timeout, connection refused)
      if (attempt < fullConfig.maxRetries) {
        const delay = calculateBackoff(attempt, fullConfig);
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }
    }
  }

  // All retries exhausted
  throw lastError || new Error('Request failed after retries');
}

/**
 * Authentication interceptor that adds bearer token to requests
 * 
 * @param token - JWT access token to include in Authorization header
 */
export function setAuthToken(token: string | null): void {
  if (token) {
    // Store token for middleware to use
    sessionStorage.setItem('access_token', token);
  } else {
    sessionStorage.removeItem('access_token');
  }
}

/**
 * Request middleware to add authentication headers and correlation IDs
 */
apiClient.use({
  onRequest: async ({ request }) => {
    // Add bearer token if available
    const token = sessionStorage.getItem('access_token');
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }

    // Add correlation ID for request tracing
    const correlationId = crypto.randomUUID();
    request.headers.set('X-Correlation-ID', correlationId);

    return request;
  },
  onResponse: async ({ response }) => {
    // Check for 401 Unauthorized and trigger token refresh
    if (response.status === 401) {
      // Emit event for auth store to handle token refresh/logout
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    return response;
  },
});

/**
 * Helper to extract error message from API error response
 * 
 * @param error - Error object from API response
 * @returns Human-readable error message
 */
export function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    // StandardError format from API
    if ('detail' in error && typeof error.detail === 'string') {
      return error.detail;
    }
    // Array of errors
    if ('detail' in error && Array.isArray(error.detail)) {
      return error.detail
        .map((e: { msg?: string }) => e.msg || 'Unknown error')
        .join(', ');
    }
    // Fallback
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
  }
  return 'An unexpected error occurred';
}

// Re-export types for convenience
export type { paths };
export type { components, operations } from './schema';
